import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
  ConnectorStatusMessage,
  HeartbeatMessage,
  RemoteStartRejectedMessage,
  StationStatusMessage,
} from '../common/interfaces/socketMessageTypes';

export interface WebSocketContextTypes {
  connectWebSocket: (id: string, connectorId?: number | null) => void;
  lastMessage: WebSocketMessage | null;
  readyState: ReadyState;
  // Bumps each time the socket transitions back to OPEN after it had previously
  // been OPEN — i.e. a real reconnect, not the initial connection. Consumers
  // can subscribe to this to re-fetch a state snapshot from REST.
  reconnectedAt: number;
}

type WebSocketMessage =
  | StationStatusMessage
  | ConnectorStatusMessage
  | HeartbeatMessage
  | RemoteStartRejectedMessage;

export const WebSocketContext = createContext<WebSocketContextTypes | null>(
  null,
);

export const WebSocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [reconnectedAt, setReconnectedAt] = useState(0);
  const hasOpenedRef = useRef(false);

  const { lastMessage: messageFromSocket, readyState } = useWebSocket(
    socketUrl,
    {
      shouldReconnect: () => true,
      // react-use-websocket gives up after 20 attempts by default (~100s at the
      // default 5s interval) and then never tries again. A charging screen must
      // outlast any backend restart or tunnel flap, so keep retrying for as long
      // as the page is open, backing off to 15s so a long outage doesn't hammer
      // the server.
      reconnectAttempts: Number.MAX_SAFE_INTEGER,
      reconnectInterval: (attempt: number) =>
        Math.min(1000 * 2 ** attempt, 15000),
    },
  );

  useEffect(() => {
    if (messageFromSocket) {
      setLastMessage(JSON.parse(messageFromSocket.data));
    }
  }, [messageFromSocket]);

  // Detect a real reconnect: we've been OPEN before, dropped, and just came back.
  // Don't fire on the very first OPEN — that's the initial connection, not a recovery.
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      if (hasOpenedRef.current) {
        setReconnectedAt(Date.now());
      }
      hasOpenedRef.current = true;
    }
  }, [readyState]);

  // Stable identity: consumers depend on this in effects (e.g. Station.fetchData).
  // Recreating it every render would make those effects re-run on each socket
  // message (heartbeats included), reloading the page in a loop.
  const connectWebSocket = useCallback(
    (id: string, connectorId?: number | null) => {
      const SOCKET_URL = import.meta.env.VITE_APP_SOCKET_URL;
      if (id) {
        let url = `${SOCKET_URL}&stationId=${id}`;
        if (connectorId) {
          url += `&connectorId=${connectorId}`;
        }
        setSocketUrl(url);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({ connectWebSocket, lastMessage, readyState, reconnectedAt }),
    [connectWebSocket, lastMessage, readyState, reconnectedAt],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
